import * as React from 'react'
import { useEffect } from 'react'
import { ChatWidget } from '@infranodus/infranodus-ai'
import { UserContext } from '../../UserContext'

import { useAiChatWidgetData } from '../../hooks/useAiChatWidgetData'
import { useUsage } from '../Paywall/useUsage'
import { usePaywallStore } from '../Paywall/usePaywallStore'
import {
    isPaywallCode,
    chatsLeftLine,
    reasonLineFor,
} from '../Paywall/paywallCopy'
import { useIsOldAnalyticsOpen } from '../../hooks/useIsOldAnalyticsOpen'
import { useZustand } from '../../hooks/useZustand'
import { usePanelStore } from '../../stores/usePanelStore'
import {
    useOnboardingStore,
    selectGuideSuppressesAiAutoOpen,
} from '../../stores/useOnboardingStore'
import { useFilterStore } from '../../stores/useFilterStore'
import {
    useContextsStore,
    selectIsPublicView,
    selectIsOwnView,
} from '../../stores/useContextsStore'
import {
    isHideAlways,
    isHideAi,
    isGraphOnlyMode,
} from '../LeftMenuMigration/legacyBridge'
import { getVariantName } from '../../appVariant'

// Product variant the host is running ('infranodus' | 'keywordgraph'),
// resolved from window.__VARIANT_CONFIG__. Read once at module eval to
// match how LazyGraph sources APP_VARIANT.
const APP_VARIANT = getVariantName()

const currentContextTypeSelector = state => state.currentContextType

const setUseNewAiModuleSelector = state => state.setUseNewAiModule
const autoAddFromAiChatSelector = state => state.autoAddFromAiChat
const addUserMessagesToGraphSelector = state => state.addUserMessagesToGraph
const willAutoSendSelector = state => state.willAutoSend

const isAiChatOpenSelector = state => state.isAiChatOpen
const setAiChatOpenSelector = state => state.setAiChatOpen
const toggleAiChatSelector = state => state.toggleAiChat

// Import CSS for the AI chat widget
if (typeof window !== 'undefined') {
    import('@infranodus/infranodus-ai/css')
}

export default function AIChat() {
    const { flags = {}, flagsLoading = false } = React.useContext(UserContext)
    const isPublic = useContextsStore(selectIsPublicView)

    const {
        selectedTopics,
        selectedConcepts,
        selectedGraph,
        insightClusters,
        allClusters,
        forceRequestMode,
        filteredStatementIds,
        selectedStatementIds,
        statementsSelectionMode,
        stopWords,
    } = useAiChatWidgetData()

    const resetForceRequestMode = useZustand(
        state => state.resetForceRequestMode
    )
    const autoAddFromAiChat = useZustand(autoAddFromAiChatSelector)
    const addUserMessagesToGraph = useZustand(addUserMessagesToGraphSelector)
    const currentContextType = useZustand(currentContextTypeSelector)
    const willAutoSend = useZustand(willAutoSendSelector)

    // Forwarded to `<ChatWidget onReady={...}>` so the widget signals when
    // it has finished hydrating (chats + context-settings fetched). The
    // auto-message gate in `home/index.jsx` waits on `isAiChatReady` so
    // `setForceRequestMode` lands after the widget can actually send,
    // not while its session is still in-flight.
    const handleChatWidgetReady = React.useCallback(() => {
        useZustand.getState().setIsAiChatReady(true)
    }, [])

    // The onboarding guide waits for the answer, not the send: it holds its
    // step until the widget reports the request settled. The free-tier usage
    // line re-reads the counters at the same moment (a chat may have started).
    const handleResponseComplete = React.useCallback(() => {
        const onboardingState = useOnboardingStore.getState()
        if (onboardingState.status === 'active') {
            onboardingState.recordAiResponse()
        }
        usePaywallStore.getState().loadUsage(true)
    }, [])

    const {
        inputNotice,
        handleRequestError,
        handleActiveChatChange,
    } = useAiPaywallNotice()
    const chatModelToUse = React.useMemo(() => {
        try {
            const chatParam = new URLSearchParams(window.location.search).get(
                'chat'
            )
            if (!chatParam) return null

            // Backward compatibility with old short chat codes.
            if (chatParam === 'gpt4') return 'gpt-4o'
            if (chatParam === '1') return 'gpt-4o-mini'

            return chatParam
        } catch (error) {
            return null
        }
    }, [])

    // Open-state lives in usePanelStore so the PanelToggles' AI button can
    // read/write it. Mutations from useEffects below also go through the store.
    const isOpen = usePanelStore(isAiChatOpenSelector)
    const setAiChatOpen = usePanelStore(setAiChatOpenSelector)
    const toggleAiChat = usePanelStore(toggleAiChatSelector)

    const isAnalyticsOpen = useIsOldAnalyticsOpen()

    const setUseNewAiModule = useZustand(setUseNewAiModuleSelector)

    // Mount-time initial open state. One-shot — writes to the store without
    // persisting, so we don't pollute aiChatClosed[pathname] with a value the
    // user didn't explicitly toggle. If the user clicked the bar's AI button
    // during the pre-mount window (graph still loading), this overwrites their
    // intent — accepted regression for the ≤1s graph-load window.
    useEffect(() => {
        let initial
        if (isHideAlways() || isHideAi() || isGraphOnlyMode()) {
            // Soft-close: keep the AI toggle button visible (PanelToggles
            // still renders), but the panel itself must boot closed.
            // `hide_always` closes both panels; `hide_ai` closes just this one.
            // In graph-only embeds (`hide_max`) the chat must boot closed too —
            // it stays mounted only so an in-graph AI action can pop it open
            // (ChatWidget renders nothing while closed).
            initial = false
        } else if (autoAddFromAiChat) {
            initial = true
        } else if (
            selectGuideSuppressesAiAutoOpen(useOnboardingStore.getState()) &&
            selectIsOwnView(useContextsStore.getState())
        ) {
            // First-session guide: skip the unrequested auto-open (the auto summary would land
            // on the opening toasts). Only the open is skipped — a real AI action still opens
            // the panel below. Safe to read here: AIChat mounts after the guide hydrates.
            initial = false
        } else if (window.innerWidth <= 600) {
            initial = false
        } else {
            let aiChatClosed = {}
            try {
                const raw = localStorage.getItem('aiChatClosed')
                if (raw) aiChatClosed = JSON.parse(raw)
            } catch {
                aiChatClosed = {}
            }
            initial = !aiChatClosed[location.pathname]
        }
        setAiChatOpen(initial, { persist: false })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Reset forceRequestMode after it's been received by ChatWidget
    useEffect(() => {
        if (forceRequestMode.name !== 'none') {
            console.log(
                '🔄 forceRequestMode received, scheduling reset in microtask queue'
            )
            if (!autoAddFromAiChat) {
                setAiChatOpen(true, { persist: false })
            }
            resetForceRequestMode()
        }
    }, [forceRequestMode, resetForceRequestMode, autoAddFromAiChat])

    useEffect(() => {
        if (autoAddFromAiChat) {
            setAiChatOpen(true, { persist: false })
        }
    }, [autoAddFromAiChat])

    // Handle hiding the old AI buttons when new AI module is enabled
    React.useEffect(() => {
        // List of element IDs to hide when new AI module is active
        const elementsToHide = [
            'aibutton', // Original AI button
            'aipanel', // AI panel (if opened)
            // 'analyticsbutton', // Analytics button
            // 'paraphraseGraph', // AI: Summarize Topics
            // 'developTopicalState', // AI: Question to Diversify
            // 'showGapContent', // AI: Idea (gap)
            // 'showquestion', // AI: Question (gap)
            'show_seo_relations_idea', // AI: Idea (SEO relations)
            'show_seo_relations_question', // AI: Question (SEO relations)
            'show_seo_idea', // AI: Idea (SEO)
            'show_seo_question', // AI: Question (SEO)
            'ai_graph_info_panel', // AI (from notes)
            // 'showlatent',
            // 'showlatent_idea',
            'generateBasedOnGraphForAiGapLabel',
            // 'developStructureAI',
            // 'developTopicalStateButton',
            // 'developStructureAIButton',
            'generateBasedOnGraphForRelated',
            'generateBasedOnGraphLabel',
            'generateBasedOnGraphForRelatedLabel',
            'generateBasedOnGraphForLatentLabel',
            'generateBasedOnGraphForLatent',
            'generateBasedOnGraphForAiGap',
            'generateBasedOnGraphForCommunitiesLabel',
            'generateBasedOnGraphForRelatedLabel',
        ]

        elementsToHide.forEach(elementId => {
            const element = document.getElementById(elementId)
            if (element) {
                // TODO: remove isPublic after flag is enabled
                if (flags.new_ai_module || isPublic) {
                    element.classList.add('hidden')
                    setUseNewAiModule(true)
                } else {
                    element.classList.remove('hidden')
                }
            }
        })

        if (typeof posthog != 'undefined') {
            posthog.capture('ai_widget_action', {
                action: 'ai_widget_activated',
            })
        }
    }, [flags.new_ai_module])

    useEffect(() => {
        if (typeof posthog != 'undefined' && flags.new_ai_module) {
            posthog.capture('ai_widget_action', {
                action: isOpen ? 'ai_widdget_open' : 'ai_widdget_closed',
            })
        }
    }, [isOpen])

    // The AI chat widget emits `aiSelectGraph` when the user picks an
    // Auto-Generate mode, so the graph can highlight the topic clusters that
    // AI request used. We mirror the incoming topic ids into the filter
    // store's highlight, reusing the same logic as LazyGraph's
    // `onSelectedTopics`. `concepts` is reserved (always [] for now) and
    // ignored this round.
    useEffect(() => {
        if (!window.myEventEmitter) return

        const onAiSelectGraph = payload => {
            const incomingIds = new Set(
                (payload?.topics || []).map(id => String(id))
            )

            // Idempotency guard / loop-safety: setHighlight flows back into
            // the widget as a new `selectedTopics` prop, so re-applying an
            // identical selection must be a no-op.
            const currentTopics =
                useFilterStore.getState().highlight.topics || []
            const currentIds = new Set(currentTopics.map(t => String(t.id)))
            if (
                currentIds.size === incomingIds.size &&
                [...currentIds].every(id => incomingIds.has(id))
            ) {
                return
            }

            const loadedTopics =
                useFilterStore.getState().availableOptions.topics || []
            const newTopics = loadedTopics.filter(topic =>
                incomingIds.has(String(topic.id))
            )

            // Ids not found because the graph isn't loaded yet (or topics
            // empty) — skip rather than clear the existing highlight.
            if (newTopics.length === 0) return

            useFilterStore.getState().setHighlight({ topics: newTopics })
        }

        myEventEmitter.on('aiSelectGraph', onAiSelectGraph)
        return () => {
            if (window.myEventEmitter) {
                myEventEmitter.removeListener('aiSelectGraph', onAiSelectGraph)
            }
        }
    }, [])

    // Don't render anything if feature flags are still loading
    if (flagsLoading) {
        return <div className="ai-module-placeholder">Loading...</div>
    }

    // Render only when there's a graph to chat about. Anonymous viewers reach
    // this branch via `isPublic`. The `flags.new_ai_module` gate was removed —
    // the new AI module is the only path now.
    if (!isPublic && !selectedGraph) {
        return null
    }

    const isDarkMode = document.documentElement.hasAttribute('theme')

    return (
        <ChatWidget
            darkMode={isDarkMode}
            title={
                APP_VARIANT === 'keywordgraph' ? 'AI Insights' : 'AI Insights'
            }
            placeholder="Ask me anything about this content..."
            theme="light"
            appVariant={APP_VARIANT}
            graphContextType={currentContextType}
            willAutoSend={willAutoSend}
            onReady={handleChatWidgetReady}
            onResponseComplete={handleResponseComplete}
            inputNotice={inputNotice}
            onRequestError={handleRequestError}
            onActiveChatChange={handleActiveChatChange}
            className={
                isAnalyticsOpen ? `ai-chat-widget-analytics` : `ai-chat-widget`
            }
            isOpen={isOpen}
            onToggle={toggleAiChat}
            selectedGraph={selectedGraph}
            selectedTopics={selectedTopics}
            selectedConcepts={selectedConcepts}
            insightClusters={insightClusters}
            allClusters={allClusters}
            forceRequestMode={forceRequestMode}
            filteredStatementIds={filteredStatementIds}
            selectedStatementIds={selectedStatementIds}
            statementsSelectionMode={statementsSelectionMode}
            stopWords={stopWords}
            graphologyGraph={{}}
            autoAddToGraphFromChat={autoAddFromAiChat}
            addUserMessagesToGraph={addUserMessagesToGraph}
            modelToUse={chatModelToUse || undefined}
            autoAddGraphTags={
                autoAddFromAiChat
                    ? { user: 'you', ai: 'ai generated' }
                    : undefined
            }
        />
    )
}

// What the widget shows around its input for a free account —
// "N free chats left" before a chat, and the blocked block (with Upgrade) once
// either allowance is spent.
// The widget talks to /react/ai with fetch, so its 402s bypass the axios
// interceptor; onRequestError is the AI path's equivalent.
function useAiPaywallNotice() {
    const { usage, isFree, remaining } = useUsage()
    const openUpgradeDialog = usePaywallStore(state => state.openUpgradeDialog)
    const [activeChat, setActiveChat] = React.useState({
        chatId: null,
        userMessageCount: 0,
    })

    const handleActiveChatChange = React.useCallback(state => {
        setActiveChat(current =>
            current.chatId === state.chatId &&
            current.userMessageCount === state.userMessageCount
                ? current
                : state
        )
    }, [])

    const handleRequestError = React.useCallback(
        error => {
            const body = error && error.body
            if (error?.status === 402 && body && isPaywallCode(body.code)) {
                openUpgradeDialog(body)
                usePaywallStore.getState().loadUsage(true)
            }
        },
        [openUpgradeDialog]
    )

    const inputNotice = React.useMemo(() => {
        if (!isFree || !usage) return null
        const upgrade = code => ({
            kind: 'blocked',
            text: reasonLineFor({
                code,
                limit:
                    code === 'QUOTA_CHATS'
                        ? usage.chatsStarted.limit
                        : usage.messagesPerChat.limit,
            }),
            actionLabel: 'Upgrade',
            onAction: () => openUpgradeDialog({ code }),
        })
        if (activeChat.chatId) {
            const limit = usage.messagesPerChat?.limit
            if (typeof limit !== 'number') return null
            const left = limit - activeChat.userMessageCount
            return left <= 0 ? upgrade('QUOTA_CHAT_MESSAGES') : null
        }
        if (remaining.chats === null) return null
        return remaining.chats <= 0
            ? upgrade('QUOTA_CHATS')
            : { kind: 'usage', text: chatsLeftLine(remaining.chats) }
    }, [isFree, usage, remaining.chats, activeChat, openUpgradeDialog])

    return { inputNotice, handleRequestError, handleActiveChatChange }
}
